import type {
  CompanyStatus,
  CreateMyCompanyRequest,
  MyCompanyListResponse,
  OnboardingCompanyResponse,
  SetupState
} from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { disconnectSocket, getSocket } from '@/api/socket'
import {
  createMyCompany,
  getMyCompanies,
  postActiveCompanyChange,
  switchActiveCompany
} from '@/domains/companies'

export const MY_COMPANIES_QUERY_KEY = ['me', 'companies'] as const

export function useMyCompanies() {
  return useQuery<MyCompanyListResponse>({
    queryKey: MY_COMPANIES_QUERY_KEY,
    queryFn: ({ signal }) => getMyCompanies(signal),
    staleTime: 30_000
  })
}

/** where a switch is headed. Super carries no company. */
export type SwitchTarget =
  | { scope: 'super' }
  | { scope: 'company'; companyId: string }

/** shared post-switch cleanup: reset socket, invalidate every query, then
 *  route based on whether the current URL still means anything.
 *
 *  route-preserve rule: if both source and target are active-status, the
 *  current path is still meaningful in the new tenant (or it's a detail
 *  route like /suppliers/<slug> which 404s cleanly in the new scope,
 *  acceptable per the v1 contract). leave the URL alone and let the
 *  page's own loader refetch against the new cookie.
 *
 *  cross-boundary rule: if source or target is onboarding-status, or either
 *  side is Super, the sets of valid URLs differ (the onboarding shell and the
 *  super surface each have their own routes). navigate to '/' and let
 *  RootRedirect pick the right landing.
 */
async function softTransitionAfter(
  queryClient: ReturnType<typeof useQueryClient>,
  navigate: ReturnType<typeof useNavigate>,
  stayOnRoute: boolean
): Promise<void> {
  disconnectSocket()
  getSocket()
  await queryClient.invalidateQueries()
  if (!stayOnRoute) {
    void navigate('/', { replace: true })
  }
}

/** decide the route-preserve rule from the React Query cache at
 *  mutation-call time. the cache has what it needs because useMyCompanies and
 *  SetupProvider both populate it on app boot, and a switcher can't trigger
 *  before its own list renders.
 */
function stayOnRouteFor(
  queryClient: ReturnType<typeof useQueryClient>,
  target: SwitchTarget
): boolean {
  const setup = queryClient.getQueryData<SetupState>(['setup'])
  /** super routes and company routes are disjoint, so any move to or from
   *  Super always lands back on '/'. */
  if (target.scope === 'super' || setup?.scope === 'super') return false

  const memberships = queryClient.getQueryData<MyCompanyListResponse>(
    MY_COMPANIES_QUERY_KEY
  )
  const sourceCompanyId = setup?.companyId ?? null
  const sourceStatus: CompanyStatus | null =
    setup?.companyStatus ??
    memberships?.items.find(item => item.id === sourceCompanyId)?.status ??
    null
  const targetStatus: CompanyStatus | null =
    memberships?.items.find(item => item.id === target.companyId)?.status ??
    null
  return sourceStatus === 'active' && targetStatus === 'active'
}

export function useSwitchCompany() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: (target: SwitchTarget) => switchActiveCompany(target),
    onSuccess: async (_data, target) => {
      /** notify peer tabs BEFORE the local transition so they can run
       *  their own invalidate+reconnect concurrently. peers don't
       *  navigate (they keep their own route), see use-active-company-sync. */
      postActiveCompanyChange({
        type: 'switched',
        companyId: target.scope === 'super' ? null : target.companyId
      })
      await softTransitionAfter(
        queryClient,
        navigate,
        stayOnRouteFor(queryClient, target)
      )
    }
  })
}

export function useCreateMyCompany() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: (body: CreateMyCompanyRequest) => createMyCompany(body),
    onSuccess: async (result: OnboardingCompanyResponse) => {
      postActiveCompanyChange({
        type: 'created',
        companyId: result.company.id
      })
      /** newly-created companies are always status='onboarding', so the
       *  cross-boundary navigate('/' → /onboarding) always fires. encoded
       *  literally rather than read from cache so it can't drift if the
       *  server contract changes.
       */
      await softTransitionAfter(queryClient, navigate, false)
      return result
    }
  })
}
